import React from 'react';
import { Link } from 'react-router-dom';
import { companyInfo, services, industries, testimonials, clientLogos } from '../data/mockData';
import { ArrowRight, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';

const Home = () => {
  return (
    <div>
      <SEO 
        title="Professional Security Guard Services in Miami, Florida"
        description="Bu Security provides trusted professional security guard services in Miami, Florida. Offering armed guards, unarmed guards, mobile patrols, event security, and surveillance monitoring for businesses and residential properties."
        keywords="Miami security guards, Florida security services, armed security guards Miami, unarmed guards Florida, event security Miami, mobile patrol services, surveillance monitoring, business security Miami, residential security Florida"
      />
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1485230405346-71acb9518d9c?w=1920&h=1080&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-[#1e3a52]/80" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          {/* Shield Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-32 h-32 relative">
              <svg viewBox="0 0 100 120" className="w-full h-full">
                <defs>
                  <linearGradient id="shieldGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#5ba4c8" />
                    <stop offset="100%" stopColor="#2c4a62" />
                  </linearGradient>
                </defs>
                <path 
                  d="M50 5 L90 20 V55 C90 85 50 110 50 110 C50 110 10 85 10 55 V20 L50 5Z" 
                  fill="url(#shieldGradient)" 
                  stroke="#5ba4c8" 
                  strokeWidth="2"
                />
                <path 
                  d="M50 25 L70 35 V55 C70 70 50 85 50 85 C50 85 30 70 30 55 V35 L50 25Z" 
                  fill="#1e3a52"
                />
                <path 
                  d="M35 50 L45 60 L65 40" 
                  stroke="#5ba4c8" 
                  strokeWidth="4" 
                  fill="none" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
          
          <p className="text-[#5ba4c8] text-sm tracking-widest uppercase mb-4">
            bu security
          </p>
          
          <h1 className="text-white text-4xl md:text-6xl font-bold mb-6 tracking-wide">
            BU<br />SECURITY
          </h1>
          
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            {companyInfo.description}
          </p>
          
          <Link to="/quote">
            <Button 
              variant="outline" 
              className="border-2 border-[#5ba4c8] text-[#5ba4c8] bg-transparent hover:bg-[#5ba4c8] hover:text-white px-8 py-6 text-lg font-semibold transition-all duration-300"
            >
              Get a Quote
            </Button>
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="bg-[#5ba4c8] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1e3a52] mb-12 tracking-wider">
            OUR SERVICES
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <Link 
                key={service.id} 
                to={`/services/${service.id}`}
                className="group relative h-[400px] overflow-hidden rounded-lg"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${service.image}')` }}
                />
                <div className="absolute inset-0 bg-[#1e3a52]/70 group-hover:bg-[#1e3a52]/80 transition-colors" />
                
                <div className="relative h-full flex flex-col justify-center items-center text-center p-6">
                  <h3 className="text-white text-xl font-bold uppercase tracking-wider mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 text-sm mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {service.shortDesc}
                  </p>
                  <div className="border-2 border-white text-white px-6 py-2 text-sm font-medium hover:bg-white hover:text-[#1e3a52] transition-all">
                    More Info
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-[#1e3a52] mb-12 tracking-wider">
            INDUSTRIES SERVICED
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {industries.map((industry) => (
              <Link 
                key={industry.id} 
                to={`/industries/${industry.id}`}
                className="group relative h-[200px] overflow-hidden"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${industry.image}')` }}
                />
                <div className="absolute inset-0 bg-[#1e3a52]/50 group-hover:bg-[#1e3a52]/70 transition-colors" />
                
                <div className="relative h-full flex items-center p-6">
                  <h3 className="text-white text-xl font-bold">
                    {industry.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <Link to="/industries">
              <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7] text-white px-8 py-3">
                See All Industries
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="bg-gray-100 py-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-[#1e3a52] mb-8">
            Who We Work With
          </h2>
          
          <div className="flex gap-8 animate-marquee">
            {[...clientLogos, ...clientLogos].map((client, index) => (
              <div 
                key={index} 
                className="flex-shrink-0 w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-md"
              >
                <span className="text-[#1e3a52] font-bold text-xs text-center">{client.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="bg-[#1e3a52] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Quote size={48} className="text-[#5ba4c8] mx-auto mb-6" />
          <blockquote className="text-white text-lg md:text-xl italic mb-6">
            "{testimonials[0].text}"
          </blockquote>
          <div className="text-[#5ba4c8]">
            <p className="font-bold">{testimonials[0].name}</p>
            <p className="text-sm text-gray-400">{testimonials[0].company}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#5ba4c8] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Secure Your Property?
          </h2>
          <p className="text-white/80 mb-8">
            Contact us today for a free consultation and quote
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/quote">
              <Button className="bg-white text-[#1e3a52] hover:bg-gray-100 px-8 py-3">
                Request a Quote
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#1e3a52] px-8 py-3">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
